'use client';

import React, { createContext, useContext, useCallback, useMemo, useState, useRef } from 'react';
import type { Chat, ChatSummary } from '../types/index.js';
import { toTimestamp } from '../types/index.js';

/**
 * ChatDataContext
 * 
 * PHASE 4 OPTIMIZATION: Centralized management of chat data and operations
 * 
 * This context handles:
 * - Chat list state (chats, pagination)
 * - Selected chat state
 * - Chat operations (create, update, delete, archive)
 * - Optimistic updates to prevent unnecessary re-renders
 * 
 * Note: Streaming state is now in ChatStreamingContext to prevent re-render cascades
 * 
 * Usage:
 * 1. Wrap your component tree with <ChatDataProvider>
 * 2. Use useChatData() hook to access and update chat data
 */

export interface ChatDataContextType {
  // Chat list state
  chats: ChatSummary[];
  selectedChat: Chat | null;
  selectedChatId: string | null;
  
  // Pagination state
  chatListCursor: string | null;
  hasMoreChats: boolean;
  isLoadingMoreChats: boolean;
  
  // Message pagination state
  messageCursor: string | null;
  hasMoreMessages: boolean;
  isLoadingOlderMessages: boolean;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Operations
  setChats: (chats: ChatSummary[] | ((prev: ChatSummary[]) => ChatSummary[])) => void;
  setSelectedChat: (chat: Chat | null) => void;
  setSelectedChatId: (chatId: string | null) => void;
  setChatListCursor: (cursor: string | null) => void;
  setHasMoreChats: (hasMore: boolean) => void;
  setIsLoadingMoreChats: (isLoading: boolean) => void;
  setMessageCursor: (cursor: string | null) => void;
  setHasMoreMessages: (hasMore: boolean) => void;
  setIsLoadingOlderMessages: (isLoading: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Optimistic update for chat title after first message
  updateChatTitle: (chatId: string, title: string) => void;
  
  // Optimistic update for chat last message timestamp
  updateChatLastMessage: (chatId: string, timestamp: string) => void;
}

const ChatDataContext = createContext<ChatDataContextType | undefined>(undefined);

interface ChatDataProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for ChatDataContext
 * 
 * PHASE 4 OPTIMIZATION: Memoizes context value to prevent unnecessary re-renders
 * Only re-renders consumers when actual data changes, not on every parent render
 */
export function ChatDataProvider({ children }: ChatDataProviderProps) {
  // Chat list state
  const [chats, setChatsState] = useState<ChatSummary[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  
  // Pagination state
  const [chatListCursor, setChatListCursor] = useState<string | null>(null);
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  
  // Message pagination state
  const [messageCursor, setMessageCursor] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref to track pending updates to avoid race conditions
  const pendingUpdatesRef = useRef<Set<string>>(new Set());

  // Wrapper for setChats that handles both direct values and callbacks
  const setChats = useCallback((chats: ChatSummary[] | ((prev: ChatSummary[]) => ChatSummary[])) => {
    if (typeof chats === 'function') {
      setChatsState(chats);
    } else {
      setChatsState(chats);
    }
  }, []);

  /**
   * Optimistically update chat title in the list
   * This prevents re-renders while waiting for the full chat reload
   */
  const updateChatTitle = useCallback((chatId: string, title: string) => {
    setChats(prev => 
      prev.map(chat => 
        chat.id === chatId ? { ...chat, title } : chat
      )
    );
  }, [setChats]);

  /**
   * Optimistically update chat last message timestamp
   * This prevents re-renders while waiting for the full chat reload
   */
  const updateChatLastMessage = useCallback((chatId: string, timestamp: string) => {
    setChats(prev => 
      prev.map(chat => 
        chat.id === chatId ? { ...chat, lastMessageAt: toTimestamp(timestamp), updatedAt: toTimestamp(timestamp) } : chat
      )
    );
  }, [setChats]);

  // PHASE 4 OPTIMIZATION: Memoize the context value to prevent unnecessary re-renders of consumers
  // Only re-renders when actual data changes, not on every parent render
  const value = useMemo(
    () => ({
      chats,
      selectedChat,
      selectedChatId,
      chatListCursor,
      hasMoreChats,
      isLoadingMoreChats,
      messageCursor,
      hasMoreMessages,
      isLoadingOlderMessages,
      isLoading,
      error,
      setChats,
      setSelectedChat,
      setSelectedChatId,
      setChatListCursor,
      setHasMoreChats,
      setIsLoadingMoreChats,
      setMessageCursor,
      setHasMoreMessages,
      setIsLoadingOlderMessages,
      setIsLoading,
      setError,
      updateChatTitle,
      updateChatLastMessage,
    }),
    [
      chats,
      selectedChat,
      selectedChatId,
      chatListCursor,
      hasMoreChats,
      isLoadingMoreChats,
      messageCursor,
      hasMoreMessages,
      isLoadingOlderMessages,
      isLoading,
      error,
      setChats,
      setSelectedChat,
      setSelectedChatId,
      setChatListCursor,
      setHasMoreChats,
      setIsLoadingMoreChats,
      setMessageCursor,
      setHasMoreMessages,
      setIsLoadingOlderMessages,
      setIsLoading,
      setError,
      updateChatTitle,
      updateChatLastMessage,
    ]
  );

  return (
    <ChatDataContext.Provider value={value}>
      {children}
    </ChatDataContext.Provider>
  );
}

/**
 * Hook to access and update chat data
 * 
 * Returns all chat state and operations
 * 
 * Example:
 * ```tsx
 * const { selectedChat, updateChatTitle } = useChatData();
 * 
 * // Update chat title optimistically
 * updateChatTitle('chat-123', 'New Title');
 * ```
 */
export function useChatData(): ChatDataContextType {
  const context = useContext(ChatDataContext);
  if (!context) {
    throw new Error('useChatData must be used within ChatDataProvider');
  }
  return context;
}


